import React from "react";
import { useNavigate } from "react-router-dom";
import WantListingThumb from "@/components/WantListingThumb";
import { wantListingLinkProps } from "@/helpers/wantListingLink";
import { compactPrice } from "@/helpers/compactPrice";
import { hourLabel } from "@/helpers/resultsChart";
import "./ResultsRealtime.scss";

const priceLine = (row) => {
  if (row.priceMin && row.priceMax) return `${compactPrice(row.priceMin)} to ${compactPrice(row.priceMax)}`;
  if (row.priceMax) return `Up to ${compactPrice(row.priceMax)}`;
  if (row.priceMin) return `From ${compactPrice(row.priceMin)}`;
  return "Buyer Want-Listing";
};

const placeLine = (row) => [row.city, row.state].filter(Boolean).join(", ");

export const listingTitle = (row) => row.title || [priceLine(row), placeLine(row)].filter(Boolean).join(" in ");

const ResultsRealtime = ({ realtime, topListings = [], live = null, activityLabel, latest = [] }) => {
  const navigate = useNavigate();
  const hours = Array.isArray(realtime?.hours) && realtime.hours.length ? realtime.hours : null;
  const total = hours ? Number(realtime.total) || 0 : null;
  const max = hours ? Math.max(0, ...hours) : 0;
  const top = topListings.slice(0, 3);

  return (
    <aside className="results-card results-realtime" aria-label="Realtime">
      <p className="results-realtime__title">Realtime</p>
      <p className="results-realtime__live">
        <span className="results-realtime__pulse" aria-hidden="true" />
        Updating live
      </p>

      {live ? (
        <div className="results-realtime__block">
          <p className="results-realtime__big">{Number(live.value || 0).toLocaleString("en-US")}</p>
          <p className="results-realtime__label">{live.label}</p>
          {live.detail ? <p className="results-realtime__detail">{live.detail}</p> : null}
        </div>
      ) : null}

      {hours ? (
        <div className="results-realtime__block">
          <p className="results-realtime__big">{total.toLocaleString("en-US")}</p>
          <p className="results-realtime__label">{activityLabel} · Last 48 hours</p>
          <div className="results-realtime__bars" role="img" aria-label={`${total} in the last 48 hours`}>
            {hours.map((value, index) => (
              <span
                key={index}
                className={`results-realtime__bar${value ? "" : " is-zero"}`}
                style={{ height: value && max ? `${Math.max(8, (value / max) * 100)}%` : undefined }}
                data-tip={`${hourLabel(realtime.start, index)} · ${value}`}
              />
            ))}
          </div>
          <div className="results-realtime__axis" aria-hidden="true">
            <span>48h ago</span>
            <span>Now</span>
          </div>
          {total === 0 ? <p className="results-realtime__quiet">No activity in the last 48 hours</p> : null}
        </div>
      ) : null}

      {top.length ? (
        <div className="results-realtime__block">
          <p className="results-realtime__head">
            <span>Top Buyer Want-Listings</span>
            <span>Views</span>
          </p>
          <ul className="results-realtime__list">
            {top.map((row) => (
              <li key={row.id}>
                <a className="results-realtime__row is-link" {...wantListingLinkProps(navigate, row)}>
                  <WantListingThumb map={row.map} width={64} height={40} title={listingTitle(row)} />
                  <span className="results-realtime__name">{listingTitle(row)}</span>
                  <span className="results-realtime__count">{Number(row.views || 0).toLocaleString("en-US")}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : latest.length ? (
        <div className="results-realtime__block">
          <p className="results-realtime__head">
            <span>Latest</span>
          </p>
          <ul className="results-realtime__list">
            {latest.slice(0, 3).map((item) => (
              <li key={item.key} className="results-realtime__row is-text">
                <span className="results-realtime__name">{item.title}</span>
                <span className="results-realtime__when">{item.when}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </aside>
  );
};

export default ResultsRealtime;
